// Pipeline Copilot — server-built visual blocks. These render SPECS the server
// computed (never model-authored) with the house's existing hand-rolled SVG
// charts: the highlighted pipeline stage diagram (from the deterministic
// StageVerdict) and per-visual charts (coverage / bars / trend). No chart
// library is introduced — every number was Rust-computed and verified.

import { Fragment } from 'react';
import { BriefingTrendChart } from '@/components/briefing-trend-chart';
import { CoverageChart, MetricChart } from '@/components/charts';
import { Sparkline } from '@/components/primitives';
import { bandColor } from '@/lib/system-metrics';
import { STAGE_LABEL, stageNodeClass, visualComponent, visualFmt } from '@/lib/copilot';
import type { CpTipApi } from '@/components/copilot';
import type { Band } from '@/lib/system-metrics';
import type { StageVerdict, Visual } from '@/api/types.gen';

// ── highlighted pipeline stage diagram (the "where" answer) ─────────────────

export function CPStageDiagram({ verdict, tipApi }: { verdict: StageVerdict; tipApi?: CpTipApi }) {
  return (
    <div className="cp-stage-diagram" role="img" aria-label={verdict.summary}>
      {verdict.stages.map((s, i) => (
        <Fragment key={s.stage}>
          {i > 0 && <span className="cp-stage-arrow">→</span>}
          <div
            className={stageNodeClass(s)}
            onMouseEnter={
              tipApi
                ? (e) =>
                    tipApi.show(
                      e,
                      <>
                        <span className="cp-tip-k">{s.band}</span>
                        {s.detail}
                      </>,
                    )
                : undefined
            }
            onMouseLeave={tipApi ? tipApi.hide : undefined}
          >
            <span className="cp-stage-name">
              <span className="cp-stage-dot" style={{ background: bandColor(s.band as Band) }} />
              {STAGE_LABEL[s.stage] ?? s.stage}
            </span>
            {s.most_implicated && <span className="cp-stage-cause">▲ likely cause</span>}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

// ── one chart block ─────────────────────────────────────────────────────────

export function CPVisual({ visual }: { visual: Visual }) {
  const series = visual.series[0];
  const values = series ? series.values : [];
  const comp = visualComponent(visual);

  let chart;
  if (comp === 'coverage') {
    // The wire carries only the % series; synthesize counts/totals (out of 100)
    // so every bucket reads "has data" and the tooltip shows "N/100 · N%".
    chart = (
      <CoverageChart
        data={values}
        dayLabels={visual.buckets}
        counts={values.map((v) => Math.round(v))}
        totals={values.map(() => 100)}
        avg={visual.avg ?? undefined}
      />
    );
  } else if (comp === 'sparkline' && visual.series.length > 1) {
    chart = <BriefingTrendChart buckets={visual.buckets} series={visual.series} unit={visual.unit} label={visual.label} />;
  } else if (comp === 'sparkline') {
    chart = <Sparkline data={values} w={240} h={44} color={series?.color ?? 'var(--accent)'} />;
  } else {
    const f = visualFmt(visual.unit);
    chart = (
      <MetricChart data={values} dayLabels={visual.buckets} fmtTip={f} fmtAxis={f} fmtPeak={f} />
    );
  }

  return (
    <div className="cp-visual" data-cp-visual={visual.id}>
      <div className="cp-visual-hd">
        {visual.label}
        {visual.caption ? <span className="cp-visual-cap"> · {visual.caption}</span> : null}
      </div>
      {chart}
    </div>
  );
}

/** The strip of server-built charts rendered between claims and the footer. */
export function CPVisualStrip({ visuals }: { visuals: ReadonlyArray<Visual> }) {
  if (!visuals.length) return null;
  return (
    <div className="cp-visual-strip">
      {visuals.map((v) => (
        <CPVisual key={v.id} visual={v} />
      ))}
    </div>
  );
}
